'use client';

import { useCallback, useMemo } from 'react';
import { useGlobalContext } from '@/store/contexts/global-context';
import { COOKIES } from '@/utils/cookies';
import { getCookieValue } from '@/utils/cookies/client';
import { useStore } from '@/store/store';
import { useAccountUm } from '@/hooks/use-account-um';
import { POST_LOGIN_ACTION_TYPE, setPostLoginAction } from '@/utils/post-login-action/post-login-action';
import { launchGameWindow } from '@/utils/promotion-game-launch/promotion-game-launch';
import { isGameLauncherUrl } from '@fanduel/rewards-hub-client/promotion-utils';
import { isQuicklaunchLauncherUrl } from './utils';
import { useLauncherUrlResolution } from '@/hooks/use-launcher-url-resolution';
import { PromotionGameLauncher } from './promotion-game-launcher';
import { runGameLaunchWithLoginGeolocationGate } from '@/logic/geolocation/login-geolocation-gate';
import { getGameLaunchMode } from '@/utils/url/game-launch-mode';

export const usePromotionLauncher = () => {
    const { isMobile, isLoggedIn, isXsellWrapper, isAndroidWrapper, isIosWrapper, isDesktop } = useGlobalContext();
    const launcherResolution = useLauncherUrlResolution();
    const addGameWindow = useStore((state) => state.addGameWindow);
    const { triggerLogin } = useAccountUm();

    const launchGame = useCallback(
        (url: string) => {
            const region = getCookieValue(COOKIES.REGION);
            launchGameWindow(url, {
                isMobile,
                isXsellWrapper,
                isDesktop,
                isAndroidWrapper,
                addGameWindow,
                region,
                launcherResolution
            });
        },
        [isDesktop, isMobile, isXsellWrapper, isAndroidWrapper, addGameWindow, launcherResolution]
    );

    const handleGameLaunch = useCallback(
        (url: string) => {
            if (isLoggedIn) {
                runGameLaunchWithLoginGeolocationGate(() => launchGame(url));
                return;
            }

            setPostLoginAction({
                type: POST_LOGIN_ACTION_TYPE.LAUNCH_GAME,
                url,
                launchMode: getGameLaunchMode(url, {
                    ...launcherResolution,
                    isMobile,
                    isAndroidWrapper,
                    isIosWrapper,
                    isDesktop
                })
            });
            triggerLogin();
        },
        [isMobile, isLoggedIn, launchGame, triggerLogin, launcherResolution, isAndroidWrapper, isIosWrapper, isDesktop]
    );

    const launcher = useMemo(
        () => ({
            getLauncher(url?: string) {
                if (!url || (!isGameLauncherUrl(url) && !isQuicklaunchLauncherUrl(url))) return null;
                return function GameLauncherWrapper(button: React.ReactNode) {
                    return (
                        <PromotionGameLauncher url={url} onLaunch={handleGameLaunch}>
                            {button}
                        </PromotionGameLauncher>
                    );
                };
            },
            getLauncherType(url: string): 'gameLauncher' | null {
                return isGameLauncherUrl(url) || isQuicklaunchLauncherUrl(url) ? 'gameLauncher' : null;
            },
            handleGameLaunch
        }),
        [handleGameLaunch]
    );

    return launcher;
};
